import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { FieldItem, Values } from '@lib/interfaces/form';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import UserOption from '@lib/components/Select/components/UserOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import SectorOption from '@lib/components/Select/components/SectorOption';
import SectorSingleValue from '@lib/components/Select/components/SectorSingleValue';
import SiteOption from '@lib/components/Select/components/SiteOption';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import formStyles from '@lib/assets/styles/Form.module.scss';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';

const sectorsSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
  Option: SectorOption,
  SingleValue: SectorSingleValue,
} as unknown as SelectComponents;

const sitesSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
  Option: SiteOption,
} as unknown as SelectComponents;

export default function getAccessValidationFields({
  t,
  sectorsLoadOptions,
  sitesLoadOptions,
  approverLoadOptions,
  isFullAdmin = true,
  isPlanLocked,
  hasSitesAccess = true,
  hasUsersAccess = true,
}: {
  t: TFunction<'translation', undefined>;
  sectorsLoadOptions: GetLoadOptions;
  sitesLoadOptions: GetLoadOptions;
  approverLoadOptions: GetLoadOptions;
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  hasSitesAccess?: boolean;
  hasUsersAccess?: boolean;
}): FieldItem[] {
  // Whole section is a Full-Admin-only upgrade affordance when locked.
  if (isPlanLocked && !isFullAdmin) return [];
  const fields: FieldItem[] = [];
  if (hasSitesAccess) {
    fields.push(
      {
        name: DocumentFormFieldNames.Sectors,
        label: t('sectors'),
        element: Select,
        isOptionalFn: () => true,
        formItemClassName: formStyles.formColumn,
        componentProps: {
          isAsync: true,
          isMulti: true,
          getLoadOptions: sectorsLoadOptions,
          defaultOptions: false,
          isClearable: true,
          maxMenuHeight: 200,
          usePortal: false,
          components: sectorsSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-sectors-tooltip') }
            : undefined,
        },
      },
      {
        name: DocumentFormFieldNames.Sites,
        label: t('sites'),
        element: Select,
        isOptionalFn: () => true,
        formItemClassName: formStyles.formColumn,
        componentProps: {
          isAsync: true,
          isMulti: true,
          getLoadOptions: sitesLoadOptions,
          defaultOptions: false,
          isClearable: true,
          maxMenuHeight: 200,
          usePortal: false,
          components: sitesSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-sites-tooltip') }
            : undefined,
        },
      },
    );
  }
  if (hasUsersAccess) {
    fields.push({
      name: DocumentFormFieldNames.Approver,
      label: t('approver'),
      element: Select,
      isOptionalFn: () => true,
      isUseWatch: true,
      getFieldKeyFn: (values: Values) => {
        const categoryId = _get(
          values,
          [DocumentFormFieldNames.Category, 'value'],
          '',
        );
        return categoryId
          ? `${DocumentFormFieldNames.Approver}-category-${categoryId}`
          : DocumentFormFieldNames.Approver;
      },
      componentProps: {
        isAsync: true,
        getLoadOptions: approverLoadOptions,
        defaultOptions: false,
        isClearable: true,
        maxMenuHeight: 200,
        usePortal: true,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
        disabled: isPlanLocked,
        disabledTooltipProps: isPlanLocked
          ? { body: t('unlock-approver-tooltip') }
          : undefined,
      },
    });
  }
  if (fields.length === 0) return [];
  return [
    {
      name: DocumentFormFieldNames.AccessValidationTitle,
      customRender: () => (
        <FieldTitleRender
          key="accessValidation"
          title={t('access-and-validation')}
          tooltipText={t('access-and-validation-desc')}
          isBlockStart
        />
      ),
    },
    ...fields,
  ];
}
